import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowRight, CreditCard, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");

export default function PayRequestPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/api/payments/requests/public/${token}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [token]);

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data: res } = await axios.post(`${API_BASE}/api/payments/requests/public/${token}/checkout`, {
        origin_url: window.location.origin,
      });
      window.location.href = res.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impossible de lancer le paiement");
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 space-y-4">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-10 w-full rounded-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
        <Card className="w-full max-w-md text-center">
          <CardContent className="p-10">
            <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
            <h1 className="font-heading text-xl font-bold text-slate-900">Lien invalide</h1>
            <p className="text-slate-500 mt-2 text-sm">Cette demande de paiement n'existe pas ou a expiré.</p>
            <Button asChild className="mt-6 rounded-full">
              <Link to="/login">Se connecter</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const paid = data.status === "paid";
  const payable = data.payable && !paid;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center p-6">
      <Card className="w-full max-w-lg shadow-xl border-slate-100" data-testid="pay-request-page">
        <div className="bg-blue-600 px-8 py-5 rounded-t-xl">
          <p className="font-heading text-white text-sm font-bold tracking-widest uppercase">COACH.</p>
          <p className="text-blue-100 text-sm mt-1">Demande de paiement</p>
        </div>
        <CardContent className="p-8">
          <p className="text-sm text-slate-500">Coach</p>
          <p className="font-semibold text-slate-900 text-lg">{data.coach_name}</p>

          <div className="mt-6 p-5 rounded-2xl bg-slate-50 border border-slate-100">
            <p className="font-semibold text-slate-900">{data.label}</p>
            {data.description && <p className="text-sm text-slate-500 mt-2">{data.description}</p>}
            <p className="text-4xl font-bold text-blue-600 mt-4">{Number(data.amount).toFixed(2)} €</p>
          </div>

          <div className="mt-4 flex items-center gap-2">
            {paid ? (
              <Badge className="bg-green-100 text-green-700 border-green-200">
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Payé
              </Badge>
            ) : payable ? (
              <Badge variant="secondary">En attente de paiement</Badge>
            ) : (
              <Badge variant="destructive">Expirée ou annulée</Badge>
            )}
          </div>

          {payable && (
            <Button
              onClick={handlePay}
              disabled={paying}
              className="w-full mt-8 h-12 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-base gap-2"
              data-testid="public-pay-btn"
            >
              <CreditCard className="w-5 h-5" />
              {paying ? "Redirection..." : <>Payer par carte <ArrowRight className="w-4 h-4" /></>}
            </Button>
          )}

          <p className="text-xs text-slate-400 text-center mt-6">
            Paiement sécurisé via Stripe · <Link to="/login" className="text-blue-600 hover:underline">Connexion élève</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}